import {
  createTranscript,
  DEFAULT_STALL_MS,
  searchCount,
  selectMcpFailure,
  selectSessionUsage,
  stallState,
  type Client,
  type ClearedToolResults,
  type SessionUsage,
  type TaskCard,
  type TranscriptConfirmation,
  type TranscriptContext,
  type TranscriptError,
  type TranscriptManualCompaction,
  type TranscriptMcpFailure,
  type TranscriptMessage,
  type TranscriptSummarizing,
  type TranscriptSummary,
  type TranscriptToolCall,
  type TranscriptTruncation,
  type TruncatedToolResult,
} from '@openharness/client'
import type {
  MessageSkill,
  ModeId,
  Session,
  SessionStatus,
  TodoList,
  UserToolConfirmationEventInput,
} from '@openharness/protocol'
import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import { describeError } from '../lib/errors'
import { noteAuthenticationError } from '../lib/auth-store'
import { useSessionRefresh } from './use-session-refresh'
import { useSettings } from './use-settings'

/**
 * One open session: its transcript, its status, and the two things a user can do to it.
 *
 * The state flow is the one the client's transcript reducer is written for:
 *
 * 1. on mount (and whenever the session changes), load the log with `events.iterate` and fold
 *    it into a fresh transcript;
 * 2. then follow live with `events.stream(sessionId, { deltas: true, afterSeq: lastSeq })`,
 *    starting exactly where the history stopped, so nothing is replayed and nothing is missed;
 * 3. on unmount (leaving the session), abort the stream — the client ends the iteration
 *    quietly, so there is nothing to catch.
 *
 * Reloading the page is the same code path as opening the session for the first time, which is
 * why a reload restores the full history and resumes the stream: the log is the state.
 *
 * `send` and `interrupt` are the only writes. Both report failure through {@link requestError}
 * (an `ApiError` from the server) rather than throwing, so a screen can render it inline.
 */
export interface SessionView {
  /** The session resource: title, agent, timestamps. `null` until it has loaded. */
  readonly session: Session | null
  /** The conversation, in order. */
  readonly messages: readonly TranscriptMessage[]
  /**
   * The "Conversation summarized" dividers still in the conversation (epic #277, K10; #280),
   * each carrying where it draws among the messages.
   *
   * A summary supersedes nothing, so the history above a divider stays on screen; only a rewind
   * takes one back, and the reducer drops it with the branch it replaced.
   */
  readonly summaries: readonly TranscriptSummary[]
  /** The summary being written right now, or `null` (epic #277, C2; #280). */
  readonly summarizing: TranscriptSummarizing | null
  /** How full the context was at the last real model request, or `null` (epic #277, #280). */
  readonly context: TranscriptContext | null
  /** The newest item a request had to shorten to fit, or `null` (epic #277, K6; #280). */
  readonly truncation: TranscriptTruncation | null
  /** The tool calls the conversation holds, in order (epic #303, X5; #308). */
  readonly toolCalls: readonly TranscriptToolCall[]
  /** The task list the model last wrote, or `null` (epic #303, X5; #305; #308). */
  readonly todos: TodoList | null
  /**
   * The background tasks this chat ran, in the order their cards draw (epic #340, S9; #346).
   *
   * One per `session.task_started` the log holds — the type, the description, the status, what
   * the parent and the child said to each other and the latest result — each carrying the
   * `position` of the `agent` call that started it, which is where its card is drawn.
   */
  readonly tasks: readonly TaskCard[]
  /** The tool results the newest request shortened, or `[]` (epic #303, X9; #306; #308). */
  readonly truncatedToolResults: readonly TruncatedToolResult[]
  /** The old tool results the newest request cleared, or `null` (epic #303, X9; #306; #308). */
  readonly clearedToolResults: ClearedToolResults | null
  /**
   * The decisions the reader has made about calls that waited on them, in log order
   * (epic #303, X6; #309; #310).
   *
   * A waiting call with a confirmation of its own has been answered and the turn is on its
   * way — which is why the prompts read this rather than only a local "sent" flag: a reload
   * lands on the same state a live tab shows.
   */
  readonly confirmations: readonly TranscriptConfirmation[]
  /** The call ids whose confirmation this tab has sent and the log has not echoed yet. */
  readonly answering: readonly string[]
  /** How many `web_search` calls this chat made (epic #303, X5; #305; #308). */
  readonly searches: number
  /**
   * The manual compaction the log last asked for, or `null` (epic #277, K8; #283).
   *
   * `pending` is the "Compacting…" state between the reader's `/compact` and the brain's answer;
   * the outcome is what a notice is drawn from for a `nothing_to_summarize` or `failed` run,
   * and is simply `summarized` once the divider is there.
   */
  readonly manualCompaction: TranscriptManualCompaction | null
  /** Whether the agent is working. */
  readonly status: SessionStatus
  /**
   * Whether a running turn has gone quiet for {@link DEFAULT_STALL_MS} (#378, P4).
   *
   * A crash's stall is otherwise indistinguishable from a slow model. The rule is the client's
   * `stallState`, over this hook's own arrival clock; the indicator disappears when an event
   * arrives or the turn ends.
   */
  readonly stalled: boolean
  /** Whether the event stream is reconnecting (#378, P4); outranks {@link stalled}. */
  readonly reconnecting: boolean
  /** The latest `session.error` in the log, until a reply supersedes it. */
  readonly lastError: TranscriptError | null
  /**
   * The newest MCP server a turn could not use, or `null` (epic #303, X10; #313).
   *
   * Its own field rather than a read of `lastError`: the turn carries on without that server, so
   * the reply that clears the error must not take the notice with it.
   */
  readonly mcpFailure: TranscriptMcpFailure | null
  /** The `seq` the transcript has folded in: where a resumed stream would pick up. */
  readonly lastSeq: number
  /** The history is still being loaded. */
  readonly loadingHistory: boolean
  /** A failed request — history, send or interrupt — as shown inline. */
  readonly requestError: string | null
  /** Whether the session was deleted (#111, epic #116, U5): its stream ended, its log is gone. */
  readonly deleted: boolean
  /**
   * The model the log last said the session runs (epic #116, U1): the id a `user.message`
   * switched it to, or the one the newest request ran, seeded from the session's own model
   * (#268). `null` only for a transcript nothing has told anything.
   */
  readonly model: string | null
  /**
   * What the session has spent (epic #245, A2; issue #247): the running totals the log reports,
   * or the same totals derived from the replies in it — a session stored before `session.usage`
   * existed has none of those events and still answers.
   *
   * Tokens only: the money is computed where the catalog's prices are, which is the screen's
   * business and not this hook's.
   */
  readonly usage: SessionUsage
  /**
   * Send a message. While the agent is running this is a steering message.
   *
   * `options.model` (`{ id }`) switches the session's model from this message on (U3), and
   * `options.rewindTo` restarts the session from a message the reader edited (#238,
   * "edit and resend"): the rewind and the message are one request, so the transcript is never
   * left without the edit. The answer says whether the message was stored, so a composer can
   * keep the text on a failure — including the 409 a rewind gets while a turn is running.
   */
  readonly send: (
    text: string,
    options?: { model?: string; skill?: MessageSkill; rewindTo?: number },
  ) => Promise<boolean>
  /**
   * Ask the brain to compact the older history now — `/compact [instructions]` (#283).
   *
   * `instructions` is the reader's guidance for the summary, or `undefined` for none. The
   * stored request is folded into the transcript at once, so the composer clears and the log
   * shows the ask; the outcome arrives on the stream like any other event. Answers whether the
   * request was stored, so the composer keeps the text on a failure.
   */
  readonly compact: (instructions?: string) => Promise<boolean>
  /** Ask the running session to stop. */
  readonly interrupt: () => Promise<void>
  /**
   * Stop a background task a chat started, by interrupting the **child** session it runs in
   * (epic #340, S9; issue #346).
   *
   * The reader's own Stop, and the only route there is: `agent_stop` is a tool the *parent model*
   * calls, and nothing in the API lets a client write a `session.task_status`. So the card's Stop
   * is the child's `user.interrupt` — which is exactly what a person may do to a non-interactive
   * session (S1).
   *
   * **What the card reads afterwards is `stopped`.** A child's interrupted turn ends like any
   * other turn ends, and the brain's child-turn delivery announces one as a **stop**
   * (`packages/brain/src/agents.ts`'s `childTurnDelivery`): a `result` saying the user stopped the
   * helper, with `session.task_status { stopped }` beside it (S7; #348). The card follows the
   * parent's log through `tasksFromEvents`, so it reads `stopped` with no rule of its own.
   */
  readonly stopTask: (childSessionId: string) => Promise<void>
  /**
   * Answer calls that are waiting on the reader (epic #303, X6; #309; #310).
   *
   * One `user.tool_confirmation` per call — an approval, a denial, or an `ask_user` call's
   * answers — sent in one batch so a "Deny all" is one append. The server checks each one
   * against the log before storing it, and the brain turns it into the result the call is
   * owed; nothing is written here. The stored events arrive on the stream, so the transcript
   * moves on its own; answers `false` when the request failed, so a prompt can stay put.
   */
  readonly respond: (events: readonly UserToolConfirmationEventInput[]) => Promise<boolean>
  /** Clear {@link requestError}. */
  readonly dismissError: () => void
}

/**
 * Follow one session: history into the transcript, then the live stream.
 *
 * The caller is expected to remount (or change `sessionId`) when the user opens a different
 * session; the transcript lives exactly as long as the hook call does.
 */
export function useSession(client: Client, sessionId: string): SessionView {
  const transcript = useMemo(() => createTranscript(), [client, sessionId])
  // **The transcript's updates are transitions.**
  //
  // The stream folds a whole network read's worth of events in one turn of the event loop — a
  // resumed connection's backlog, a big tool result and the turn it ends, a fast provider's
  // deltas — and the store notifies on every one of them. `useSyncExternalStore` answers each
  // notification with a **synchronous** update (`forceStoreRerender`, React's own sync lane),
  // so a burst is a run of synchronous commits with more of them already pending; React counts
  // those as nested updates and gives up past fifty with "Maximum update depth exceeded" (React
  // #185). The throw comes out of the store's notify call — inside `transcript.apply` in the
  // stream loop below, whose `catch` reports it as a failed *request* — which is exactly what a
  // chat showed: a "Request failed" banner whose message was React's own minified #185.
  //
  // A transition is the right lane for this data: the conversation is not urgent, only the
  // newest state matters, and React coalesces the updates that arrive before it renders instead
  // of committing each one. The reader's own input — a keystroke in the composer, a click — stays
  // urgent and can interrupt a long transcript render.
  //
  // The subscription is React's own pattern, with the two things `useSyncExternalStore` does
  // internally that a hand-rolled one must not forget: the subscribe happens in a layout effect
  // (before the paint, and after every render, so the window between the render and the
  // subscription is closed by the catch-up read below), and the snapshot the render used is
  // compared with the store's newest — an event folded in *between* the two would otherwise
  // leave the screen a state behind until the next event arrived.
  const [, rerender] = useReducer((count: number) => count + 1, 0)
  const state = transcript.getState()
  useLayoutEffect(() => {
    const unsubscribe = transcript.subscribe(() => {
      startTransition(rerender)
    })
    if (!Object.is(state, transcript.getState())) {
      startTransition(rerender)
    }
    return unsubscribe
  }, [transcript, state])

  // Whether the event stream is reconnecting (#378, P4), reported by the client's stream loop.
  // Beside the transcript rather than in it: it is a fact about the connection, not the log, and
  // a replay of the same events has no way to know it.
  const [reconnecting, setReconnecting] = useState(false)
  // When the last **streamed** event arrived, in epoch milliseconds. History and this tab's own
  // writes do not count: the indicator asks whether the server has said anything lately. The
  // clock ticks once a second while a turn runs, which is what re-evaluates the quiet window
  // without putting wall-clock time into the pure reducer.
  const lastEventAt = useRef<number>(Date.now())
  const [clock, setClock] = useState(() => Date.now())

  useEffect(() => {
    if (state.status !== 'running') {
      return undefined
    }
    const timer = setInterval(() => {
      setClock(Date.now())
    }, 1_000)
    return () => {
      clearInterval(timer)
    }
  }, [state.status])

  const stall = stallState({
    status: state.status,
    lastEventAt: lastEventAt.current,
    now: clock,
    // The reconnect is reported as it happens, so it outranks the quiet window.
    reconnecting,
    thresholdMs: DEFAULT_STALL_MS,
  })

  const [loaded, setLoaded] = useState<Session | null>(null)
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [requestError, setRequestError] = useState<string | null>(null)
  // A failure of our own is described with the server the client is pointed at, so a request
  // that never arrived can say where it did not arrive.
  const { serverUrl } = useSettings()
  // The session as the freshest read of it: the mount fetch below, or — once this chat has
  // said something — the re-read that turns the agent's name into the title the server
  // derived (#35). Both the sidebar row and this header read that one copy.
  const { sessions: fresh, refresh } = useSessionRefresh(client)
  const session = fresh.get(sessionId) ?? loaded

  useEffect(() => {
    const controller = new AbortController()

    const follow = async (): Promise<void> => {
      setLoadingHistory(true)
      try {
        const opened = await client.sessions.get(sessionId, { signal: controller.signal })
        if (controller.signal.aborted) {
          return
        }
        setLoaded(opened)
        // Seed the transcript with the model the session runs before replaying its log (#268):
        // the first message a reader switches the model on then has something to differ from,
        // so the marker the composer draws matches the one a live view drew. The replay
        // corrects the baseline from each request's span, so a resumed chat lands on the same
        // markers however much history it loaded.
        transcript.reset({ model: opened.model.id })

        for await (const event of client.sessions.events.iterate(
          sessionId,
          {},
          { signal: controller.signal },
        )) {
          transcript.apply(event)
        }
      } catch (caught) {
        if (!controller.signal.aborted && !noteAuthenticationError(client, caught)) {
          setRequestError(describeError(caught, { serverUrl }))
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingHistory(false)
        }
      }

      if (controller.signal.aborted) {
        return
      }

      try {
        for await (const event of client.sessions.events.stream(sessionId, {
          deltas: true,
          afterSeq: transcript.getState().lastSeq,
          signal: controller.signal,
          onConnection: (connection) => {
            // A fresh connection starts a fresh quiet window (and clears "Reconnecting…"); a
            // dropped one is reported before the backoff, so the indicator appears at once.
            if (controller.signal.aborted) {
              return
            }
            lastEventAt.current = Date.now()
            setReconnecting(connection.state === 'reconnecting')
          },
        })) {
          // Only streamed events count for the stall indicator: a local echo of this tab's own
          // send is not the server saying anything.
          lastEventAt.current = Date.now()
          transcript.apply(event)
        }
      } catch (caught) {
        // An abort ends the iteration quietly; anything else — a revoked session, an unknown
        // session — is worth showing, because the stream is not coming back on its own. A
        // 401 is not shown at all: it signs the app out and the sign-in page takes over.
        if (!controller.signal.aborted && !noteAuthenticationError(client, caught)) {
          setRequestError(describeError(caught, { serverUrl }))
        }
      }
    }

    void follow()
    return () => controller.abort()
  }, [client, sessionId, transcript, serverUrl])

  // A session is named by the request that stores its first message (`lib/session-refresh`),
  // so a chat that has said something and still shows no title is one whose copy predates it:
  // this tab's own send, or a first message another tab just streamed in. Ask for a re-read —
  // the store decides whether there is one to make, and never makes a second.
  const saidSomething = state.messages.some((message) => message.role === 'user')
  const titled = session !== null && session.title !== null
  useEffect(() => {
    if (saidSomething && !titled) {
      refresh(sessionId)
    }
  }, [saidSomething, titled, refresh, sessionId])

  const send = useCallback(
    async (
      text: string,
      options?: { model?: string; mode?: ModeId | null; skill?: MessageSkill; rewindTo?: number },
    ): Promise<boolean> => {
      const body = text.trim()
      if (body === '') {
        return false
      }
      setRequestError(null)
      try {
        const stored = await client.sendMessage(sessionId, body, {
          ...(options?.model === undefined ? {} : { model: { id: options.model } }),
          // A mode rides the message the way a model does (#245, M6): the log records the
          // choice, and the session follows the mode from here on. `null` detaches.
          ...(options?.mode === undefined ? {} : { mode: options.mode }),
          // A slash invocation rides it too (epic #349, K7): the text is what the reader typed
          // and this is the skill it names, which is what the brain loads the instructions from.
          ...(options?.skill === undefined ? {} : { skill: options.skill }),
          ...(options?.rewindTo === undefined ? {} : { rewindTo: options.rewindTo }),
        })
        // Show the message at once instead of waiting for the stream to echo it: the client
        // returns the stored event, and the reducer drops the stream's copy of it (same `seq`).
        transcript.apply(stored)
        return true
      } catch (caught) {
        if (!noteAuthenticationError(client, caught)) {
          setRequestError(describeError(caught, { serverUrl }))
        }
        return false
      }
    },
    [client, sessionId, transcript, serverUrl],
  )

  const compact = useCallback(
    async (instructions?: string): Promise<boolean> => {
      setRequestError(null)
      try {
        const request = await client.sessions.compact(sessionId, {
          ...(instructions === undefined ? {} : { instructions }),
        })
        // The request shows at once, like a sent message: the reducer folds the stored event in
        // and the stream's echo of it is dropped by the `seq` rule. The outcome the brain writes
        // arrives later, on the stream.
        transcript.apply(request)
        return true
      } catch (caught) {
        if (!noteAuthenticationError(client, caught)) {
          setRequestError(describeError(caught, { serverUrl }))
        }
        return false
      }
    },
    [client, sessionId, transcript, serverUrl],
  )

  const [answering, setAnswering] = useState<readonly string[]>([])

  const respond = useCallback(
    async (events: readonly UserToolConfirmationEventInput[]): Promise<boolean> => {
      if (events.length === 0) {
        return false
      }
      setRequestError(null)
      setAnswering(events.map((event) => event.tool_use_id))
      try {
        await client.sessions.events.send(sessionId, [...events])
        return true
      } catch (caught) {
        if (!noteAuthenticationError(client, caught)) {
          setRequestError(describeError(caught, { serverUrl }))
        }
        return false
      } finally {
        setAnswering([])
      }
    },
    [client, sessionId, serverUrl],
  )

  const interrupt = useCallback(async (): Promise<void> => {
    setRequestError(null)
    try {
      await client.interrupt(sessionId)
    } catch (caught) {
      if (!noteAuthenticationError(client, caught)) {
        setRequestError(describeError(caught, { serverUrl }))
      }
    }
  }, [client, sessionId, serverUrl])

  const stopTask = useCallback(
    async (childSessionId: string): Promise<void> => {
      setRequestError(null)
      try {
        // The child's own interrupt; see the interface's note on what a card reads afterwards.
        await client.interrupt(childSessionId)
      } catch (caught) {
        if (!noteAuthenticationError(client, caught)) {
          setRequestError(describeError(caught, { serverUrl }))
        }
      }
    },
    [client, serverUrl],
  )

  const dismissError = useCallback(() => {
    setRequestError(null)
  }, [])

  return {
    session,
    messages: state.messages,
    summaries: state.summaries,
    summarizing: state.summarizing,
    context: state.context,
    truncation: state.truncation,
    manualCompaction: state.manualCompaction,
    toolCalls: state.toolCalls,
    todos: state.todos,
    tasks: state.tasks,
    truncatedToolResults: state.truncatedToolResults,
    clearedToolResults: state.clearedToolResults,
    confirmations: state.confirmations,
    answering,
    searches: searchCount(state.toolCalls),
    usage: selectSessionUsage(state),
    status: state.status,
    stalled: stall === 'stalled',
    reconnecting: stall === 'reconnecting',
    lastError: state.lastError,
    mcpFailure: selectMcpFailure(state),
    lastSeq: state.lastSeq,
    loadingHistory,
    requestError,
    deleted: state.deleted,
    model: state.model,
    send,
    compact,
    interrupt,
    stopTask,
    respond,
    dismissError,
  }
}
